import { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle2, Loader2, RotateCw, TimerOff, TriangleAlert, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AmountDue } from './AmountDue';
import { SnapEmbed } from './SnapEmbed';
import { useSyncPayment } from '@/hooks/mutations';
import { useMidtransSocket } from '@/hooks/useMidtransSocket';
import { gatewayName, isExpiredEnding } from '@/lib/onlinePayment';
import type { SaleStatus, SocketConfig } from '@/types/sale';

interface OnlineQrisPanelProps {
  /** Amount due for this tender; null while it is still being priced. */
  total: number | null;
  invoiceId: string;
  qrString: string | null;
  /**
   * The gateway's rendered QR image, set only when the server resolved an ASPI
   * QR (Midtrans only — Xendit and Doku send the payload string alone).
   *
   * An ASPI border lives in that PNG — a QR we draw ourselves from the payload
   * string encodes the same data but cannot carry the frame — so this is what
   * makes "QRIS (ASPI)" actually look like ASPI to the customer.
   */
  qrImageUrl?: string | null;
  /**
   * A Snap charge's session token. Opened with Midtrans's own page in place of
   * the QR — never drawn as one (it is not an EMV payload). Used only when the
   * response carries no QR to draw.
   */
  snapToken?: string | null;
  /** Status channel for this payment; null falls back to polling alone. */
  socketConfig?: SocketConfig | null;
  /**
   * When the gateway stops accepting this charge (ISO). QRIS gets 15 minutes.
   *
   * Absent on an older response — the countdown simply does not render, which
   * is the pre-existing behaviour rather than a broken one.
   */
  paymentExpire?: string | null;
  status: SaleStatus;
  onStatus: (status: SaleStatus) => void;
  onSuccess: () => void;
  onCancel: () => void;
  /**
   * Start a genuinely NEW charge after this one expired.
   *
   * Distinct from `onCancel` because the checkout reuses one idempotency key
   * for the whole session, so simply returning to the chooser and re-picking
   * QRIS replays the cached response of the charge that just died — the
   * customer would be shown the expired code again. Only the caller can mint a
   * fresh key, so it hands us this.
   */
  onRestart?: () => void;
  /**
   * The gateway's word as the CALLER already knows it.
   *
   * A fresh charge has none — the panel learns it from the push or its own
   * poll. But a payment re-opened from the order list arrives with its ending
   * already decided, and without seeding it here an expired code would render
   * as a plain decline: the exact distinction this panel exists to make.
   */
  initialGatewayStatus?: string | null;
  /**
   * Server's answer to "may this cart be charged again?", as already known by
   * the caller. The panel also learns it from its own sync results.
   */
  initialRetrySafe?: boolean;
  /**
   * Overrides what the cancel button says, in BOTH the waiting and failed
   * states. Left unset it keeps the checkout's wording. Resuming from the
   * order list needs it because there "Batalkan" would read as cancelling the
   * ORDER, and "Ganti metode" offers a chooser that is not there — both
   * buttons only close the dialog.
   */
  cancelLabel?: string;
  /**
   * The processor that took this charge (`payment.gateway`). With failover the
   * code on screen may come from Xendit or Doku rather than Midtrans, and a
   * cashier reconciling a dispute needs to know which dashboard to look in.
   */
  gateway?: string | null;
}

/** Seconds left as `mm:ss`, or `00:00` once the window has closed. */
function formatRemaining(ms: number): string {
  const secs = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`;
}

/** QRIS render, live status push, and the 15-minute charge window. */
export function OnlineQrisPanel({
  total,
  invoiceId,
  qrString,
  qrImageUrl,
  snapToken = null,
  socketConfig,
  paymentExpire,
  status,
  onStatus,
  onSuccess,
  onCancel,
  onRestart,
  initialGatewayStatus = null,
  initialRetrySafe,
  cancelLabel,
  gateway = null,
}: OnlineQrisPanelProps) {
  const { mutate: sync, isPending } = useSyncPayment();
  const [imageFailed, setImageFailed] = useState(false);
  // The gateway's own word for HOW a payment ended. Sticky: once we know the
  // code expired, a later empty response must not turn it back into a plain
  // "declined".
  const [gatewayEnding, setGatewayEnding] = useState<string | null>(initialGatewayStatus);
  // Never inferred here — see the note on `retrySafe` in types/sale.ts.
  const [retrySafe, setRetrySafe] = useState<boolean | undefined>(initialRetrySafe);
  // The same ending in gateway-neutral words (`EXPIRED`) — what Xendit/Doku
  // report. Sticky for the same reason as `gatewayEnding`.
  const [neutralEnding, setNeutralEnding] = useState<string | null>(null);

  // The gateway's answer, pushed the moment it reaches the server.
  const {
    status: pushedStatus,
    paymentTransactionStatus: pushedEnding,
    paymentStatus: pushedNeutral,
    retrySafe: pushedRetrySafe,
    connected,
  } = useMidtransSocket({
    config: socketConfig ?? null,
    open: status === 'PENDING' || status === 'INITIAL',
  });

  useEffect(() => {
    if (pushedStatus && pushedStatus !== status) onStatus(pushedStatus);
  }, [pushedStatus, status, onStatus]);

  useEffect(() => {
    if (pushedEnding) setGatewayEnding(pushedEnding);
  }, [pushedEnding]);

  useEffect(() => {
    if (pushedNeutral) setNeutralEnding(pushedNeutral);
  }, [pushedNeutral]);

  // A FAILED that arrives by push stops the poll, so the push is often the
  // only place the server's retry-safety answer comes from.
  useEffect(() => {
    if (pushedRetrySafe !== undefined) setRetrySafe(pushedRetrySafe);
  }, [pushedRetrySafe]);

  useEffect(() => {
    if (initialGatewayStatus) setGatewayEnding(initialGatewayStatus);
  }, [initialGatewayStatus]);

  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const noteResult = (res: {
    status: SaleStatus;
    paymentTransactionStatus?: string;
    retrySafe?: boolean;
    payment?: { status?: string } | null;
  }) => {
    if (res.paymentTransactionStatus) setGatewayEnding(res.paymentTransactionStatus);
    if (res.payment?.status) setNeutralEnding(res.payment.status);
    if (res.retrySafe !== undefined) setRetrySafe(res.retrySafe);
    onStatus(res.status);
  };

  const checkNow = () => {
    sync(invoiceId, { onSuccess: noteResult });
  };

  // Re-render once a second while the charge window is open. Deliberately a
  // display-only clock: it never fails the sale itself. A customer who paid at
  // 14:59 can still settle, and only the gateway knows that — so when this
  // reaches zero the panel says it is checking, and waits to be told.
  const expiresAt = useMemo(
    () => (paymentExpire ? new Date(paymentExpire).getTime() : null),
    [paymentExpire],
  );
  const [now, setNow] = useState(() => Date.now());
  const waiting = status === 'PENDING' || status === 'INITIAL';

  const msLeft = expiresAt != null && Number.isFinite(expiresAt) ? expiresAt - now : null;
  const lapsed = msLeft != null && msLeft <= 0;
  // Something the customer can actually scan. Without it there is nothing to
  // wait for, so the "waiting for payment" line below must not claim there is.
  const hasScannablePayload = Boolean(qrImageUrl) || Boolean(qrString) || Boolean(snapToken);

  useEffect(() => {
    // Stops itself at zero: there is nothing left to count down, and the
    // gateway's answer arrives by push or poll rather than from this clock.
    if (!expiresAt || !waiting || lapsed) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt, waiting, lapsed]);

  const urgent = msLeft != null && msLeft > 0 && msLeft <= 60_000;

  useEffect(() => {
    if (status === 'PENDING' || status === 'INITIAL') {
      // A BACKSTOP, not the primary signal — the socket is. It stays running
      // even while connected because the push only relays what the webhook
      // delivered, and a webhook that cannot reach the server produces a
      // perfectly healthy socket that never says anything. Slower when the
      // channel is up: every tick that finds the payment pending costs an
      // outbound gateway status call.
      timer.current = setInterval(
        () => {
          sync(invoiceId, { onSuccess: noteResult });
        },
        connected ? 15000 : 2500,
      );
    }
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
    // `noteResult` closes over onStatus only; re-creating the interval on every
    // render would reset the countdown to a full period each second.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, invoiceId, sync, onStatus, connected]);

  useEffect(() => {
    if (status === 'SUCCESS' || status === 'SETTLEMENT') {
      const t = setTimeout(onSuccess, 800);
      return () => clearTimeout(t);
    }
  }, [status, onSuccess]);

  const settled = status === 'SUCCESS' || status === 'SETTLEMENT';
  const failed = status === 'FAILED' || status === 'CANCELLED';
  // A code that ran out of time is not a decline. Nothing was refused and
  // nobody's card was touched — the cashier just needs to make a new one, and
  // saying "gagal" in red invites them to tell the customer their payment was
  // rejected. Only the gateway's own word counts here: our clock reaching zero
  // is a guess, since a payment made in the last second still settles.
  const expired = failed && isExpiredEnding(gatewayEnding, neutralEnding);
  const via = gatewayName(gateway);
  // Offering a fresh charge is a MONEY decision, so it follows the server's
  // flag rather than the panel's reading of the status. Falls back to the
  // expired-only behaviour when the flag is absent, so an older backend keeps
  // exactly the semantics it had.
  const canStartNewCharge = failed && (retrySafe ?? expired);

  return (
    <div className="flex flex-col items-center gap-4 py-3 text-center">
      <div className="rounded-lg bg-secondary px-6 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">Total tagihan</p>
        <AmountDue value={total} />
      </div>

      {settled ? (
        <div className="flex animate-gold-pulse flex-col items-center gap-2 rounded-full p-4">
          <CheckCircle2 className="h-16 w-16 text-success" />
          <p className="font-display text-xl font-bold text-success">Pembayaran diterima</p>
        </div>
      ) : expired ? (
        <div className="flex flex-col items-center gap-2">
          <TimerOff className="h-14 w-14 text-warning" />
          <p className="font-display text-lg font-semibold text-warning">Kode QRIS kedaluwarsa</p>
          <p className="max-w-xs text-sm text-muted-foreground">
            Tidak ada pembayaran yang ditolak. Buat kode baru untuk melanjutkan.
          </p>
        </div>
      ) : failed ? (
        <div className="flex flex-col items-center gap-2">
          <XCircle className="h-14 w-14 text-destructive" />
          <p className="font-display text-lg font-semibold text-destructive">Pembayaran gagal</p>
        </div>
      ) : (
        <>
          {/*
            The code is meant to be read by a customer's phone at arm's length,
            so it takes every pixel the dialog can spare.

            The bound is `100vh - 23rem`, not a fraction of the viewport. 23rem
            is what the rest of the panel measures — dialog padding, heading,
            the amount, the status line and the buttons — so the code grows to
            exactly the room that is left on ANY screen height. A flat `55vh`
            was either wasteful on a tall monitor or overflowed the buttons off
            a short one; this is the same expression at both.

            Widening the dialog would not help: the ASPI code is PORTRAIT, so
            its width follows from its height, and the dialog already has ~270px
            of width going spare. Height is the only axis that buys anything.

            The two variants are shaped differently and are sized differently
            because of it:

            - ASPI is a 600x760 PORTRAIT PNG that already carries its own
              border, QRIS/GPN logos and merchant caption. It was being forced
              into a 208x208 square, which letterboxed it to about 79% of the
              box width and shrank the scannable modules accordingly — and our
              white card framed an already-framed image a second time. It sizes
              itself from its natural aspect now, with no card around it.

            - The plain code is a square SVG we draw ourselves with no framing
              of its own, so it keeps the white card. A QR needs a quiet margin
              to scan reliably, and here that card IS the quiet margin.
          */}
          {qrImageUrl && !imageFailed ? (
            <img
              src={qrImageUrl}
              alt="Kode QRIS format ASPI"
              width={600}
              height={760}
              // Falls back to drawing the code ourselves if the image cannot
              // load, so a blocked CDN never leaves the customer with nothing
              // to scan — the payload is identical either way.
              onError={() => setImageFailed(true)}
              className="h-auto max-h-[calc(100vh-23rem)] w-auto max-w-full rounded-xl shadow-sm"
            />
          ) : qrString ? (
            <div className="w-[min(100%,calc(100vh-23rem))] rounded-xl border-2 border-border bg-white p-4 shadow-sm">
              <QRCodeSVG value={qrString} level="M" size={512} className="h-auto w-full" />
            </div>
          ) : snapToken ? (
            // Snap draws the QR itself. Its callbacks only prompt a check: the
            // sale settles on the server's word, as for a QR we draw.
            <SnapEmbed token={snapToken} onGatewayEvent={checkNow} />
          ) : (
            /*
              No payload — and none is coming. This panel only mounts once the
              charge response has landed, so an empty payload here is final, not
              a loading state.

              It used to spin for ever, and before that it drew the Midtrans
              SNAP TOKEN as a QR. A Snap token is an opaque session id, not an
              EMV payload: it has no merchant PAN and no CRC, so every banking
              app rejects it. That turned "the server returned the wrong tender"
              into "the customer scans a plausible-looking code at the counter
              and nothing happens" — the worst way for this to fail. Say what is
              wrong instead, and name the fix.
            */
            <div className="flex w-[min(100%,calc(100vh-23rem))] flex-col items-center gap-2 rounded-xl border-2 border-destructive/30 bg-destructive/5 p-6 text-center">
              <TriangleAlert className="h-10 w-10 text-destructive" aria-hidden="true" />
              <p className="font-display text-base font-semibold text-destructive">
                Kode QRIS tidak tersedia
              </p>
              <p className="text-sm text-ink-600">
                Gateway tidak mengirim kode QRIS untuk metode pembayaran ini. Gunakan metode lain
                dan laporkan ke administrator — metode “QRIS” pada terminal ini perlu dikonfigurasi
                ulang.
              </p>
              <p className="tnum text-xs text-ink-400">{invoiceId}</p>
            </div>
          )}
          {/*
            Deliberately ONE line, however much it has to say. The QR above
            sizes itself to `100vh - 23rem`, which is the measured height of
            everything else in this panel — so a second row here would push the
            buttons off a short screen.
          */}
          {/* With no code on screen there is nothing for the customer to scan,
              so a spinning "waiting for payment" line would contradict the
              error above it and read as "keep waiting, it is coming". Dropped
              from the tree rather than hidden with the `hidden` attribute —
              `display: flex` from the class list wins over `[hidden]`, so the
              attribute alone left the line fully visible. */}
          {hasScannablePayload && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            {lapsed ? (
              // Our clock ran out, but the gateway has the last word: a scan in
              // the final second still settles. So this reports what we are
              // doing, not a verdict we have not been given.
              <span>Waktu habis — memastikan ke gateway…</span>
            ) : (
              <>
                <span>Menunggu pembayaran QRIS…</span>
                {msLeft != null && (
                  <span
                    className={
                      urgent
                        ? 'font-semibold tabular-nums text-warning'
                        : 'tabular-nums text-ink-600'
                    }
                  >
                    Berlaku {formatRemaining(msLeft)}
                  </span>
                )}
              </>
            )}
            {/* Same line on purpose — see the one-line note above. */}
            {via && (
              <span className="text-xs text-ink-400" data-testid="qris-gateway">
                · via {via}
              </span>
            )}
          </p>
          )}
        </>
      )}

      <div className="flex w-full gap-3 pt-1">
        {!settled && !failed && (
          <>
            <Button variant="outline" className="flex-1" onClick={onCancel}>
              {cancelLabel ?? 'Batalkan'}
            </Button>
            <Button className="flex-1 gap-2" onClick={checkNow} disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
              Cek status
            </Button>
          </>
        )}
        {failed && (
          // An expired code needs `onRestart`, NOT `onCancel`: the latter goes
          // back to the chooser carrying the same idempotency key, so
          // re-picking QRIS replays the dead charge instead of minting one.
          <Button
            variant="outline"
            className="flex-1"
            onClick={canStartNewCharge ? (onRestart ?? onCancel) : onCancel}
          >
            {/* The label follows the CAPABILITY: promising a new code where
                the caller cannot mint one is how the checkout path went wrong
                before, and the order list genuinely cannot — it has an
                invoice, not a cart. */}
            {canStartNewCharge && onRestart ? 'Buat kode baru' : (cancelLabel ?? 'Ganti metode')}
          </Button>
        )}
      </div>
    </div>
  );
}
